import { Navigate, useBlocker, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Boxes } from 'lucide-react';
import { Button, ConfirmDialog } from '@eombor/ui';
import { t } from '../../shared/i18n/index.js';
import { usePermission } from '../../features/auth/model/usePermission.js';
import { WarehouseDetailPage } from '../../features/warehouse/ui/WarehouseDetailPage.js';
import { CellCardPage } from '../../features/warehouse/ui/CellCardPage.js';
import { WarehousesPage } from '../../features/warehouse/ui/WarehousesPage.js';
import { BuilderPage } from '../../features/warehouse-builder/ui/BuilderPage.js';
import { useBuilderStore } from '../../features/warehouse-builder/model/builder.store.js';

/**
 * Marshrut bilan sahifa orasidagi yupqa qatlam.
 *
 * Sahifalar routerni **bilmaydi**: ular `onOpenWarehouse` kabi chaqiruv
 * oladi. Shu tufayli testda router providerini qurish shart emas va
 * sahifalar boshqa kontekstda (masalan, dialog ichida) ham ishlaydi.
 *
 * Manzillar satr sifatida quriladi: bo'lim marshrutlari `NAV_ITEMS` dan
 * dinamik yasaladi, shuning uchun router turlari ularning literal nomini
 * bilmaydi (`AppShell` dagi `go()` ham xuddi shu sababdan satr oladi).
 *
 * Butun "Ombor" bo'limi shu manzil daraxti ostida turadi — mahsulot qidiruvi
 * va kirim hujjati ham. Shu tufayli yon paneldagi bitta bo'lim har qanday
 * ichki ekranda yonib turadi.
 */

export const SECTION_PATH: string = '/warehouses';

/**
 * Bo'limning STANDART ko'rinishi — butun doira bo'ylab mahsulot qidiruvi.
 *
 * Bo'lim aynan shu manzilda ochiladi: skladchi kuniga o'nlab marta "bu narsa
 * qayerda va qancha?" deb qaraydi, omborlar ro'yxatiga esa kamdan-kam
 * qaytadi.
 */
export const GLOBAL_STOCK_PATH: string = SECTION_PATH;

/** Omborlar ro'yxati — bo'limning ikkinchi ko'rinishi. */
export const LIST_PATH: string = `${SECTION_PATH}/list`;

/** Kirim hujjati — u ham ombor bo'limining ichida. */
export const RECEIPT_PATH: string = `${SECTION_PATH}/receipts/new`;

export const detailPath = (warehouseId: string): string => `${SECTION_PATH}/${warehouseId}`;

/** Ombor kartochkasining "Mahsulotlar" ko'rinishi. */
export const warehouseStockPath = (warehouseId: string): string =>
  `${detailPath(warehouseId)}/stock`;

const builderPath = (warehouseId: string): string => `${detailPath(warehouseId)}/builder`;

/**
 * SKANER KARTOCHKASI. Manzilda `id` emas, KOD turadi — aynan u etiketkaga
 * bosilgan va skanerdan aynan u keladi. Shu tufayli havolani qo'lda ham yozib
 * bo'ladi va u odam o'qiy oladigan ko'rinishda qoladi.
 */
export const cellCardPath = (warehouseId: string, code: string): string =>
  `${detailPath(warehouseId)}/cells/${encodeURIComponent(code)}`;

/**
 * Ombor kartochkasi TANLANGAN katak bilan. Bu yerda `id` ishlatiladi, chunki
 * chizma kataklarni aynan shu bilan tanidi va kod bo'yicha ikkinchi qidiruv
 * behuda bo'lardi — kartochka `id` ni allaqachon bilib turibdi.
 */
export const cellOnLayoutPath = (warehouseId: string, cellId: string): string =>
  `${detailPath(warehouseId)}?cell=${encodeURIComponent(cellId)}`;

/**
 * Katak kartochkasi. "Chizmada ko'rsatish" ombor kartochkasiga qaytaradi —
 * u yerda skanerlangan katak allaqachon tanlangan bo'lib ochiladi.
 */
export const CellCardRoute = () => {
  const navigate = useNavigate();
  const { warehouseId, code } = useParams({
    from: '/shell/warehouses/$warehouseId/cells/$code',
  });
  return (
    <CellCardPage
      warehouseId={warehouseId}
      code={code}
      onBack={() => void navigate({ to: detailPath(warehouseId) })}
      onShowOnLayout={(cellId) => void navigate({ to: cellOnLayoutPath(warehouseId, cellId) })}
    />
  );
};

/**
 * Omborlar ro'yxati — menyudagi "Omborxona" shu yerni ochadi.
 *
 * Omborlarni ko'rish huquqi (`warehouse.read`) bo'lmasa, xodim ro'yxat
 * o'rniga mahsulotlar qidiruviga yuboriladi: menyu elementi unga
 * `stock.*` ruxsati bilan ham ko'rinadi va bo'sh yoki taqiqlangan ekran
 * uning birinchi ko'rgan narsasi bo'lmasligi kerak.
 */
export const WarehousesRoute = () => {
  const navigate = useNavigate();
  const { canAnywhere } = usePermission();
  if (!canAnywhere('warehouse.read') && canAnywhere('stock.read')) {
    return <Navigate to={GLOBAL_STOCK_PATH} replace />;
  }
  return (
    <div className="flex flex-col gap-4">
      {canAnywhere('stock.read') ? (
        <div className="flex justify-start">
          <Button variant="secondary" onClick={() => void navigate({ to: GLOBAL_STOCK_PATH })}>
            <Boxes size={16} aria-hidden />
            {t('warehouse.viewStock')}
          </Button>
        </div>
      ) : null}
      <WarehousesPage onOpenWarehouse={(id) => void navigate({ to: detailPath(id) })} />
    </div>
  );
};

/**
 * Ombor kartochkasi. Ko'rinish MANZILDAN keladi, holatdan emas: skladchi
 * "shu omborning mahsulotlari" sahifasini xatcho'pga olishi va hamkasbiga
 * havola yuborishi kerak.
 */
const useDetailNavigation = (warehouseId: string) => {
  const navigate = useNavigate();
  return {
    onBack: () => void navigate({ to: LIST_PATH }),
    onOpenBuilder: (id: string) => void navigate({ to: builderPath(id) }),
    onNewReceipt: () => void navigate({ to: RECEIPT_PATH }),
    onChangeView: (view: 'structure' | 'stock') =>
      void navigate({
        to: view === 'stock' ? warehouseStockPath(warehouseId) : detailPath(warehouseId),
      }),
  };
};

export const WarehouseDetailRoute = () => {
  const { warehouseId } = useParams({ from: '/shell/warehouses/$warehouseId' });
  /* `?cell=` — skaner kartochkasidan "Chizmada ko'rsatish" bilan kelgan katak. */
  const { cell } = useSearch({ from: '/shell/warehouses/$warehouseId' });
  const nav = useDetailNavigation(warehouseId);
  return (
    <WarehouseDetailPage
      warehouseId={warehouseId}
      view="structure"
      initialCellId={cell ?? null}
      {...nav}
    />
  );
};

export const WarehouseStockRoute = () => {
  const { warehouseId } = useParams({ from: '/shell/warehouses/$warehouseId/stock' });
  const nav = useDetailNavigation(warehouseId);
  return <WarehouseDetailPage warehouseId={warehouseId} view="stock" {...nav} />;
};

/**
 * Konstruktor. Saqlanmagan qoralama bilan sahifadan chiqishga to'sqinlik
 * shu yerda turadi, sahifada emas: `useBlocker` routerning hooki va
 * `enableBeforeUnload` bilan brauzer tabini yopishni ham qamrab oladi —
 * yarim chizilgan reja bir sichqoncha bosishi bilan yo'qolmaydi.
 */
export const WarehouseBuilderRoute = () => {
  const navigate = useNavigate();
  const { warehouseId } = useParams({ from: '/shell/warehouses/$warehouseId/builder' });
  const dirty = useBuilderStore((state) => state.dirty);

  const blocker = useBlocker({
    shouldBlockFn: () => useBuilderStore.getState().dirty,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  return (
    <>
      <BuilderPage
        warehouseId={warehouseId}
        onBack={() => void navigate({ to: detailPath(warehouseId) })}
        onView3d={() => void navigate({ to: detailPath(warehouseId) })}
      />

      {/* Chizmadan chiqish — saqlanmagan ish yo'qoladi, shuning uchun
          `warn`: bu o'chirish emas, lekin qaytarib bo'lmaydi. */}
      <ConfirmDialog
        open={blocker.status === 'blocked'}
        onClose={() => blocker.reset?.()}
        onConfirm={() => {
          useBuilderStore.getState().reset();
          blocker.proceed?.();
        }}
        title={t('builder.deleteTitle')}
        description={t('builder.leaveWarning')}
        confirmLabel={t('builder.deleteConfirm')}
        cancelLabel={t('builder.cancel')}
        tone="warn"
      />
    </>
  );
};
